import React from 'react'
import { useRequiredContext } from '../utils/use-required-context'
import { ViewerConfig } from '../components/Viewer'
import { getRepoName } from '../api/app-login'
import axios from 'axios'
import { DataUpdatePayload, StrictDataUpdateV1Payload } from '../shared/data-update/data-update-schema'

type InputAppCtx = Pick<
  ViewerConfig,
  'repoURL' | 'localRepoURL' | 'bbsparqlendpoint' | 'displayChromiumTranslateErrors'
>

export type WistorAppCtx = (
  | WistorBackendAppCtx
  | SparqlOnlyAppCtx
)

export type WistorBackendAppCtx = {
  type: 'wistor-backend'
  origin: string
  repoName: string | null
  urlSparql: string
  urlUpload: string
  urlLogin: string
  urlIsLoggedIn: string
  urlAnonymousLogin: string
  makeUrl: (make: (ctx: WistorBackendAppCtx) => string) => string
  requestDataUpdate: ReturnType<typeof requestDataUpdate>
  displayChromiumTranslateErrors: boolean
}

type SparqlOnlyAppCtx = {
  type: 'sparql-only'
  urlSparql: string
  displayChromiumTranslateErrors: boolean
}

export const AppContext = React.createContext<WistorAppCtx | null>(null)
export const useAppCtx = () => useRequiredContext(AppContext)

export function AppCtxProvider(props: InputAppCtx & {children: React.ReactNode }): React.ReactNode {

  const appCtx: WistorAppCtx = React.useMemo(() => getAppCtx({
    repoURL: props.repoURL,
    localRepoURL: props.localRepoURL,
    bbsparqlendpoint: props.bbsparqlendpoint,
    displayChromiumTranslateErrors: props.displayChromiumTranslateErrors
  }), [props.repoURL, props.localRepoURL, props.bbsparqlendpoint, props.displayChromiumTranslateErrors])

  return (
    <AppContext.Provider value={appCtx}>
      {props.children}
    </AppContext.Provider>
  )

}

export function getAppCtx(input: InputAppCtx): WistorAppCtx {
  const url = window.location.hostname === 'localhost' && input.localRepoURL
    ? new URL(input.localRepoURL, window.location.origin)
    : new URL(input.repoURL, window.location.origin)
  const repoName = getRepoName(input.bbsparqlendpoint)
  const isWistorEndpoint = url.pathname.startsWith('/servlets')

  if (!isWistorEndpoint) return {
    type: 'sparql-only',
    urlSparql: url.toString(),
    displayChromiumTranslateErrors: Boolean(input.displayChromiumTranslateErrors),
  }

  const ctx: WistorBackendAppCtx = {
    type: 'wistor-backend',
    origin: url.origin,
    repoName,
    urlSparql: `${url.origin}/servlets/cgi/sparql/${repoName}`,
    urlUpload: `${url.origin}/servlets/cgi/upload/${repoName}`,
    urlLogin: getLoginUrl(repoName, url.toString(), url),
    urlAnonymousLogin: `${url.origin}/servlets/cgi/api/anon-login/${repoName}`,
    urlIsLoggedIn: `${url.origin}/servlets/cgi/isloggedin/${repoName}`,
    requestDataUpdate: requestDataUpdate(`${url.origin}/servlets/cgi/api/data-update`),
    makeUrl(make) { return make(ctx) },
    displayChromiumTranslateErrors: Boolean(input.displayChromiumTranslateErrors),
  }

  return ctx
}

export function getLoginUrl(repoName: string | null, repoUrl: string, urlOverride?: URL) {
  const url = urlOverride ?? new URL(repoUrl, window.location.origin)
  return `${url.origin}/servlets/cgi/login/${repoName}`
}

/** This is curried, because it's convenient in current use-case */
function requestDataUpdate(dataUpdateUrl: string) {
  return async function requestDataUpdate(payload: DataUpdatePayload | StrictDataUpdateV1Payload) {
    await axios.post(dataUpdateUrl, payload, {
      headers: { 'Content-Type': 'application/json' }
    })
  }
}
